import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { fmtInChurch } from "./care/text";
import { toast } from "sonner";
import { roleLabel, roleText, useOrg } from "./org";

const ACTION = {
  "org.created": () => "created the church",
  "invitation.created": (e, m) => `invited ${e.meta.email} as ${roleLabel(m, e.meta.role) || e.meta.role}`,
  "invitation.accepted": (e) => `accepted the invitation for ${e.target_name || "their email"}`,
  "invitation.resent": (e) => `resent the invitation for ${e.target_name || "someone"}`,
  "invitation.revoked": (e) => `withdrew the invitation for ${e.target_name || "someone"}`,
  "invitation.accept_denied": (e) => `tried to accept ${e.target_name || "an invitation"} with a different email (${e.meta.email})`,
  "member.role_changed": (e) => `changed ${e.target_name}'s role from ${e.meta.from} to ${e.meta.to}`,
  "confidential.granted": (e) => `gave ${e.target_name} Confidential Access`,
  "confidential.revoked": (e) => `removed Confidential Access from ${e.target_name}`,
  "member.deactivated": (e) => `paused ${e.target_name}'s access`,
  "member.reactivated": (e) => `restored ${e.target_name}'s access`,
  "member.removed": (e) => `removed ${e.target_name} from the church`,
  "person.created": (e) => `added ${e.meta.name}`,
  "person.updated": (e) => `updated ${e.meta.name}'s details`,
  "person.caregiver_assigned": (e) => (e.meta.caregiver_id ? `assigned a caregiver for ${e.meta.name}` : `removed ${e.meta.name}'s caregiver`),
  "person.care_state_changed": (e) => `changed ${e.meta.name}'s care state to ${e.meta.to}`,
  "household.created": (e) => `created ${e.meta.name}`,
  "household.renamed": (e) => `renamed a household to ${e.meta.name}`,
  "group.created": (e) => `created the ${e.meta.name} group`,
  "group.updated": (e) => `updated the ${e.meta.name} group`,
  "group.deleted": (e) => `deleted the ${e.meta.name} group`,
  "group.member_added": (e) => `added ${e.meta.person} to ${e.meta.name}`,
  "group.member_removed": (e) => `removed ${e.meta.person} from ${e.meta.name}`,
  "person_request.created": (e) => `asked to add ${e.meta.name}`,
  "person_request.added": (e) => `resolved the request to add ${e.meta.name}`,
  "person_request.declined": (e) => `declined the request to add ${e.meta.name}`,
  "settings.rhythm_changed": (e) => `changed the church Care Rhythm to ${e.meta.to} days`,
  "settings.updated": (e) => `updated church settings (${(e.meta.fields || []).map((f) => f.replace("_", " ")).join(", ")})`,
  "settings.role_labels_changed": (e) => (e.meta.restored ? "restored the default role names" : `renamed roles: ${Object.values(e.meta.changes || {}).map(([a, b]) => `${a} → ${b}`).join(", ")}`),
  "assignment.created": (e) => `assigned "${e.meta.title}" for ${e.meta.name}`,
  "assignment.reassigned": (e) => `reassigned "${e.meta.title}" for ${e.meta.name}`,
  "assignment.completed": (e) => `completed "${e.meta.title}" for ${e.meta.name}`,
  "assignment.status_changed": (e) => `marked "${e.meta.title}" for ${e.meta.name} as ${String(e.meta.to).replace("_", " ")}`,
  "assignment.updated": (e) => `updated "${e.meta.title}" for ${e.meta.name}`,
  "care_need.opened": (e) => `opened a care need for ${e.meta.name}`,
  "care_need.updated": (e) => `updated a care need for ${e.meta.name}`,
  "care_need.resolved": (e) => `resolved a care need for ${e.meta.name}`,
  "care.logged": (e) => `logged care for ${e.meta.name}`,
  "confidential.viewed": (e) => `viewed confidential notes for ${e.meta.name}`,
  "confidential.created": (e) => `added a confidential note for ${e.meta.name}`,
  "confidential.edited": (e) => `edited a confidential note for ${e.meta.name}`,
  "confidential.archived": (e) => `archived a confidential note for ${e.meta.name}`,
};

const fmt = (iso) => fmtInChurch(iso, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const FILTERS = [["all", "Everything"], ["confidential", "Confidential"], ["team", "Team & access"], ["care", "People & care"]];

export default function Audit() {
  const { api, onTenantError, membership } = useOrg();
  const [events, setEvents] = useState(null);
  const [more, setMore] = useState(false);
  const [f, setF] = useState("all");
  const fetchPage = (before) => api(`/audit?${new URLSearchParams({ ...(f !== "all" ? { category: f } : {}), ...(before ? { before } : {}) })}`);
  useEffect(() => { let live = true; setEvents(null); fetchPage().then((r) => { if (live) { setEvents(r); setMore(r.length === 100); } }).catch((e) => { onTenantError(e); toast.error(e.message); }); return () => { live = false; }; }, [api, f]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadMore = () => fetchPage(events[events.length - 1].at).then((r) => { setEvents((x) => [...x, ...r]); setMore(r.length === 100); }).catch((e) => toast.error(e.message));
  const shown = events;
  return (
    <div data-testid="audit-screen" className="max-w-3xl">
      <p className="t-label">Security</p>
      <h1 className="t-greeting mt-3">Audit history</h1>
      <p className="t-lead mt-3">Who did what, and when. Confidential note text is never shown here.</p>
      <div className="inline-flex flex-wrap gap-0.5 mt-8 p-1 rounded-[14px] bg-sunk" data-testid="audit-filters">
        {FILTERS.map(([k, l]) => <button key={k} aria-pressed={f === k} onClick={() => setF(k)} className="filter !h-10 !rounded-[10px]" data-testid={`audit-filter-${k}`}>{l}</button>)}
      </div>
      <ol className="rule-list border-y border-line mt-6" data-testid="audit-list">
        {shown?.map((e) => (
          <li key={e.id} className="py-4 flex gap-3" data-testid={`audit-${e.action}`}>
            {e.action.startsWith("confidential.") ? <Lock size={16} className="text-private mt-1 shrink-0" /> : <span className="w-4 shrink-0" />}
            <div className="min-w-0">
              <p className="t-body [overflow-wrap:anywhere]"><span className="font-semibold">{e.actor_name}</span> {roleText(membership, (ACTION[e.action] || (() => e.action))(e, membership))}</p>
              <p className="t-meta mt-1">{fmt(e.at)}</p>
            </div>
          </li>
        ))}
        {shown?.length === 0 && <li className="py-4 t-meta">Nothing recorded yet.</li>}
      </ol>
      {more && <button onClick={loadMore} className="btn btn-quiet btn-sm mt-4" data-testid="audit-load-more">Show older activity</button>}
    </div>
  );
}
